/**
 * Circuit Breaker Pattern Implementation
 * Prevents cascading failures when APIs are down
 */

class CircuitBreaker {
  constructor(options = {}) {
    this.failureThreshold = options.failureThreshold || 5;
    this.resetTimeout = options.resetTimeout || 30000; // 30 seconds
    this.monitorTimeout = options.monitorTimeout || 5000; // 5 seconds
    
    this.state = 'CLOSED'; // CLOSED, OPEN, HALF_OPEN
    this.failureCount = 0;
    this.lastFailureTime = null;
    this.nextAttempt = null;
  }

  async call(fn, fallback = null) {
    if (this.state === 'OPEN') {
      if (this.shouldAttemptReset()) {
        this.state = 'HALF_OPEN';
        console.log('[CircuitBreaker] Moving to HALF_OPEN state');
      } else {
        console.log('[CircuitBreaker] Circuit is OPEN, using fallback');
        return fallback ? fallback() : Promise.reject(new Error('Circuit breaker is OPEN'));
      }
    }

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  onSuccess() {
    this.failureCount = 0;
    this.state = 'CLOSED';
    console.log('[CircuitBreaker] Success - circuit is CLOSED');
  }

  onFailure() {
    this.failureCount++;
    this.lastFailureTime = Date.now();

    if (this.failureCount >= this.failureThreshold) {
      this.state = 'OPEN';
      this.nextAttempt = Date.now() + this.resetTimeout;
      console.log(`[CircuitBreaker] Failure threshold reached (${this.failureCount}), circuit is OPEN`);
    } else {
      console.log(`[CircuitBreaker] Failure ${this.failureCount}/${this.failureThreshold}`);
    }
  }

  shouldAttemptReset() {
    return Date.now() >= this.nextAttempt;
  }

  getState() {
    return {
      state: this.state,
      failureCount: this.failureCount,
      lastFailureTime: this.lastFailureTime,
      nextAttempt: this.nextAttempt
    };
  }

  reset() {
    this.state = 'CLOSED';
    this.failureCount = 0;
    this.lastFailureTime = null;
    this.nextAttempt = null;
    console.log('[CircuitBreaker] Circuit breaker manually reset');
  }
}

// Global circuit breakers for different API endpoints
export const apiCircuitBreakers = {
  userProfile: new CircuitBreaker({
    failureThreshold: 3,
    resetTimeout: 30000
  }),
  
  connections: new CircuitBreaker({
    failureThreshold: 3,
    resetTimeout: 20000
  }),
  
  general: new CircuitBreaker({
    failureThreshold: 5,
    resetTimeout: 60000
  })
};

export default CircuitBreaker;